import React, { useState } from 'react';
import { ref, update } from 'firebase/database';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
    FiCopy,
    FiCpu,
    FiDatabase,
    FiDownload,
    FiExternalLink,
    FiHardDrive,
    FiLayers,
    FiRefreshCw,
    FiTerminal,
    FiWifi,
} from 'react-icons/fi';

import { db } from '../../firebase';
import { getFreshness } from '../../utils/deviceData';
import { getHwFingerprint, hwShortLabel, boardKey, boardLabel } from '../../utils/hwFingerprint';

// HardwareTab — "which board is inside this box?"
//
// Renders mdm/devices/<MAC>/hw_fingerprint, pushed by Monitoring 6.0.4+ once
// per service start (and on the `hw_fingerprint` command). The one number
// that matters for firmware/OTA work is `hw_hash`: boxes with equal hashes
// share a board and can share a firmware image. Everything else on this tab
// is the evidence behind that number, grouped the way a firmware engineer
// reads it: identity, build strings, storage, kernel/boot, partitions,
// wireless/security.
//
// Every optional field hides its row when missing (the collector omits
// fields it could not read rather than writing empty strings), so a box
// with a partial fingerprint still renders cleanly.

export default function HardwareTab({ mac, device }) {
    const fp = getHwFingerprint(device);
    const navigate = useNavigate();

    if (!fp) {
        return (
            <div className="rounded-xl border border-dashed border-white/[0.08] p-10 text-center text-gray-400 space-y-4">
                <FiCpu className="mx-auto text-3xl text-gray-500" />
                <p className="text-sm">
                    No hardware fingerprint reported yet. Monitoring 6.0.4 or newer pushes it
                    automatically at every service start; older versions never will.
                </p>
                <div className="flex justify-center">
                    <CollectButton mac={mac} device={device} />
                </div>
            </div>
        );
    }

    const fresh = getFreshness(fp.collected_at);

    return (
        <div className="space-y-6">
            <HashHero fp={fp} mac={mac} device={device} freshness={fresh} navigate={navigate} />

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <Card icon={<FiCpu />} title="Identity">
                    <KV label="Platform" value={fp.platform} mono />
                    <KV label="Hardware" value={fp.hardware} mono />
                    <KV label="Board" value={fp.board} mono />
                    <KV label="Device" value={fp.product_device} mono />
                    <KV label="Product" value={fp.product_name} mono />
                    <KV label="DT compatible" value={fp.dt_compatible} mono />
                    <KV label="DT model" value={fp.dt_model} mono />
                    <KV label="Vendor model" value={fp.vendor_model} />
                    <KV label="Brand / model" value={[fp.brand, fp.model].filter(Boolean).join(' ')} />
                    <KV label="Serial" value={fp.serial} mono />
                    <KV label="App rotation" value={fp.app_rotation} mono hint={fp.app_rotation === 'force_land' ? 'OEM landscape lock - player cannot rotate' : (fp.app_rotation ? 'player controls rotation' : null)} />
                </Card>

                <Card icon={<FiLayers />} title="Build">
                    <KV label="Origin build" value={fp.origin_build} mono hint="vendor batch, survives re-brand" />
                    <KV label="Origin date" value={fp.origin_build_date} />
                    <KV label="Origin fingerprint" value={fp.origin_fingerprint} mono />
                    <KV label="Current build" value={fp.build_incremental} mono />
                    <KV label="Build ID" value={fp.build_id} />
                    <KV label="Fingerprint" value={fp.fingerprint} mono />
                    <KV label="Type / tags" value={[fp.build_type, fp.build_tags].filter(Boolean).join(' / ')} />
                    <KV label="Built by" value={[fp.build_user, fp.build_host].filter(Boolean).join('@')} mono />
                    <KV label="Android" value={fp.android_version != null ? `${fp.android_version} (SDK ${fp.sdk ?? '?'})` : null} />
                    <KV label="Security patch" value={fp.security_patch} />
                    <KV label="Treble / VNDK" value={fp.treble != null ? `${yesNo(fp.treble)} / ${fp.vndk_version || '—'}` : null} />
                    <KV label="A/B update" value={yesNo(fp.ab_update)} />
                    <KV label="Dynamic partitions" value={yesNo(fp.dynamic_partitions)} />
                    <KV label="Verity" value={fp.verity_mode || (fp.verified_boot_state ? fp.verified_boot_state : null)} />
                </Card>

                <Card icon={<FiHardDrive />} title="Storage & Memory">
                    <KV label="Storage type" value={fp.storage_type} />
                    <KV label="eMMC size" value={fp.emmc_size_gb != null ? `${fp.emmc_size_gb} GB` : null} />
                    <KV label="eMMC name" value={fp.emmc_name} mono />
                    <KV label="eMMC manufacturer" value={fp.emmc_manfid} mono hint={emmcVendor(fp.emmc_manfid)} />
                    <KV label="eMMC OEM id" value={fp.emmc_oemid} mono />
                    <KV label="eMMC date" value={fp.emmc_date} />
                    <KV label="eMMC CID" value={fp.emmc_cid} mono />
                    <KV label="Boot0 area" value={yesNo(fp.has_boot0)} />
                    <KV label="RAM" value={fp.ram_total_mb != null ? `${fp.ram_total_mb} MB` : null} />
                    <KV label="MTD" value={fp.mtd} mono />
                </Card>

                <Card icon={<FiTerminal />} title="Kernel & Boot">
                    <KV label="Kernel" value={fp.kernel} mono wrap />
                    <KV label="U-Boot" value={fp.uboot_message} mono wrap hint={fp.uboot_message ? null : 'needs su on the box'} />
                    <KV label="Boot type" value={fp.boot_type} />
                    <KV label="SELinux" value={fp.selinux} />
                    <KV label="su binary" value={yesNo(fp.su_available)} />
                    <KV label="Root used" value={yesNo(fp.root_used)} hint="collector could run su" />
                    {fp.cmdline && (
                        <Collapsible title="Kernel command line">
                            <Pre>{fp.cmdline}</Pre>
                        </Collapsible>
                    )}
                </Card>

                <Card icon={<FiDatabase />} title="Partitions">
                    <PartitionTable partitions={fp.partitions} />
                    <KV label="Dynamic (super)" value={fp.dynamic_partition_names} mono />
                </Card>

                <Card icon={<FiWifi />} title="Wireless & Collector">
                    <KV label="WiFi SDIO id" value={fp.wifi_sdio_id} mono hint={wifiVendor(fp.wifi_sdio_id)} />
                    <KV label="WiFi driver" value={fp.wifi_driver} mono />
                    <KV label="Collected" value={fp.collected_at ? `${fresh.label} · ${new Date(Number(fp.collected_at)).toLocaleString()}` : null} />
                    <KV label="Source" value={fp.source} />
                    <KV label="Collector" value={fp.collector_version != null ? `Monitoring versionCode ${fp.collector_version}` : null} />
                </Card>
            </div>

            {fp.hw_hash_basis && (
                <section className="card-chrome p-5">
                    <Collapsible title="Hash basis (exact material hw_hash was computed from)">
                        <Pre>{String(fp.hw_hash_basis).split('|').join('\n')}</Pre>
                    </Collapsible>
                </section>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Hero — the hash, big, with the actions an operator needs next to it.
// ---------------------------------------------------------------------

function HashHero({ fp, mac, device, freshness, navigate }) {
    const hash = fp.hw_hash || '—';

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(hash);
            toast.success('hw_hash copied');
        } catch {
            toast.error('Clipboard not available');
        }
    };

    return (
        <section className="card-chrome p-5 flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex items-center gap-4 min-w-0">
                <div className="brand-icon-badge shrink-0"><FiCpu /></div>
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-gray-500">Hardware group (hw_hash)</div>
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-2xl font-bold text-white tracking-wide">{hash}</span>
                        <button
                            onClick={copy}
                            className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] transition"
                            title="Copy hash"
                        >
                            <FiCopy />
                        </button>
                    </div>
                    <div className="text-sm text-gray-300 truncate" title={hwShortLabel(fp)}>{hwShortLabel(fp)}</div>
                    <div className="text-xs text-gray-400 truncate mt-0.5" title={boardKey(fp) || undefined}>
                        board: {boardLabel(fp)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        origin {fp.origin_build || '—'} · collected {freshness.label}
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                <button
                    onClick={() => navigate(`/devices?board=${encodeURIComponent(boardKey(fp) || '')}`)}
                    className="btn-secondary"
                    title="Device list filtered to the same BOARD (firmware-compatible group: device, SoC, kernel, RAM, eMMC, partition table)"
                    disabled={!boardKey(fp)}
                >
                    <FiExternalLink /> Same board
                </button>
                <button
                    onClick={() => navigate(`/devices?hw=${encodeURIComponent(hash)}`)}
                    className="btn-secondary"
                    title="Device list filtered to this exact hw_hash (also matches eMMC vendor and kernel build date)"
                    disabled={!fp.hw_hash}
                >
                    <FiExternalLink /> Same hash
                </button>
                <ExportButton mac={mac} device={device} fp={fp} />
                <CollectButton mac={mac} device={device} />
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------
// Export — the whole fingerprint as a plain-text report, so it can be
// attached to a vendor ticket or compared side by side in a diff tool.
// Sections mirror the cards on this tab; every reported field is included
// (nothing is hidden the way the UI hides empty rows).
// ---------------------------------------------------------------------

function ExportButton({ mac, device, fp }) {
    const exportTxt = () => {
        try {
            const text = buildExportText(mac, device, fp);
            const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `hw_fingerprint_${mac.replace(/:/g, '_')}_${fp.hw_hash || 'nohash'}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Let the click consume the URL before revoking it.
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success('Fingerprint exported');
        } catch (err) {
            toast.error(`Export failed: ${err?.message || err}`);
        }
    };

    return (
        <button onClick={exportTxt} className="btn-secondary" title="Download every reported field as a .txt report">
            <FiDownload /> Export .txt
        </button>
    );
}

// Field order for the report: grouped like the cards, then anything the
// collector added that this list does not know about goes into "Other" so a
// newer Monitoring never loses data in the export.
const EXPORT_SECTIONS = [
    ['Identity', ['hw_hash', 'platform', 'hardware', 'boot_hardware', 'board', 'product_device', 'product_name',
        'brand', 'model', 'manufacturer', 'vendor_model', 'serial']],
    ['Build', ['origin_build', 'origin_build_date', 'origin_build_date_utc', 'origin_fingerprint', 'origin_odm_build',
        'build_incremental', 'build_id', 'fingerprint', 'build_type', 'build_tags', 'build_user', 'build_host',
        'build_date_utc', 'android_version', 'sdk', 'security_patch', 'treble', 'vndk_version', 'ab_update',
        'dynamic_partitions', 'verity_mode', 'verified_boot_state']],
    ['Storage & Memory', ['storage_type', 'emmc_size_gb', 'emmc_name', 'emmc_manfid', 'emmc_oemid', 'emmc_date',
        'emmc_cid', 'has_boot0', 'mtd', 'ram_total_mb']],
    ['Kernel & Boot', ['kernel', 'dt_model', 'dt_compatible', 'uboot_message', 'boot_type', 'selinux',
        'su_available', 'root_used', 'cmdline']],
    ['Partitions', ['partitions', 'dynamic_partition_names']],
    ['Wireless', ['wifi_sdio_id', 'wifi_driver']],
    ['Collector', ['collected_at', 'collector_version', 'source']],
    ['Hash basis', ['hw_hash_basis']],
];

function buildExportText(mac, device, fp) {
    const lines = [];
    const push = (s = '') => lines.push(s);
    const kv = (k, v) => push(`${k.padEnd(26)} ${v}`);

    push('MusicDJ MDM - Hardware fingerprint report');
    push('='.repeat(60));
    kv('mac', mac);
    kv('device_name', device?.name || '');
    kv('customer', device?.business_name || device?.customer_name || '');
    kv('group', device?.group ? decodeURIComponent(device.group) : '');
    kv('ip_address', device?.ip_address || '');
    kv('system_version', device?.system_version || '');
    kv('monitoring_version', device?.monitoring_service_version || '');
    kv('exported_at', new Date().toISOString());
    push();

    const seen = new Set();
    EXPORT_SECTIONS.forEach(([title, keys]) => {
        const present = keys.filter(k => fp[k] !== undefined && fp[k] !== null && fp[k] !== '');
        if (!present.length) return;
        push(`[${title}]`);
        present.forEach(k => {
            seen.add(k);
            const v = fp[k];
            if (k === 'partitions' && v && typeof v === 'object') {
                push('partitions (MB):');
                Object.entries(v)
                    .sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0) || a[0].localeCompare(b[0]))
                    .forEach(([name, mb]) => push(`  ${name.padEnd(20)} ${String(mb).padStart(8)} MB`));
            } else if (k === 'hw_hash_basis') {
                push('hw_hash_basis:');
                String(v).split('|').forEach(part => push(`  ${part}`));
            } else if (k === 'collected_at' || k === 'build_date_utc' || k === 'origin_build_date_utc') {
                const n = Number(v);
                const ms = k === 'collected_at' ? n : n * 1000; // *_utc props are seconds
                const iso = Number.isFinite(n) ? new Date(ms).toISOString() : '';
                kv(k, iso ? `${v}  (${iso})` : String(v));
            } else {
                kv(k, String(v));
            }
        });
        push();
    });

    const other = Object.keys(fp).filter(k => !seen.has(k)).sort();
    if (other.length) {
        push('[Other]');
        other.forEach(k => kv(k, typeof fp[k] === 'object' ? JSON.stringify(fp[k]) : String(fp[k])));
        push();
    }

    return lines.join('\n');
}

// Same write contract as CommandsTab: set the command and mark feedback
// pending; the box resets `command` and writes its summary into `feedback`.
async function sendCommand(mac, command) {
    await update(ref(db, `mdm/devices/${mac}`), {
        command,
        feedback: 'pending',
    });
}

function CollectButton({ mac, device }) {
    const [busy, setBusy] = useState(false);
    const offline = device?.status !== 'online';

    const collect = async () => {
        setBusy(true);
        try {
            await sendCommand(mac, 'hw_fingerprint');
            toast.success(offline
                ? 'Fingerprint requested — runs when the device comes online'
                : 'Fingerprint requested — the tab updates when the box answers');
        } catch (err) {
            toast.error(`Could not send command: ${err?.message || err}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            onClick={collect}
            disabled={busy}
            className="btn-secondary"
            title="Sends the hw_fingerprint command (Monitoring 6.0.4+)"
        >
            <FiRefreshCw className={busy ? 'animate-spin' : ''} /> Collect fingerprint
        </button>
    );
}

// ---------------------------------------------------------------------
// Primitives — local copies of the Overview/Health look so this file has no
// cross-tab imports (those primitives are not exported).
// ---------------------------------------------------------------------

function Card({ icon, title, children }) {
    return (
        <section className="card-chrome p-5">
            <header className="mb-3">
                <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
                    {icon} {title}
                </h2>
            </header>
            <div className="space-y-2">{children}</div>
        </section>
    );
}

// Label/value row that hides itself when there is nothing to show.
// `wrap` allows long single-line values (kernel banner) to break instead of truncating.
function KV({ label, value, mono, wrap, hint }) {
    if (value === undefined || value === null || value === '') return null;
    return (
        <div className="grid grid-cols-[130px_1fr] items-start gap-3 text-sm">
            <span className="text-gray-400">{label}</span>
            <span className="min-w-0">
                <span
                    className={`block text-gray-200 ${mono ? 'font-mono text-[13px]' : ''} ${wrap ? 'break-all' : 'truncate'}`}
                    title={typeof value === 'string' ? value : undefined}
                >
                    {String(value)}
                </span>
                {hint && <span className="block text-[11px] text-gray-500">{hint}</span>}
            </span>
        </div>
    );
}

function Collapsible({ title, children }) {
    return (
        <details className="group mt-2">
            <summary className="cursor-pointer select-none text-xs text-gray-400 hover:text-gray-200">
                {title}
            </summary>
            <div className="mt-2">{children}</div>
        </details>
    );
}

function Pre({ children }) {
    return (
        <pre className="card-inset p-3 font-mono text-[12px] leading-relaxed text-gray-300 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
            {children}
        </pre>
    );
}

// name -> MB map from the collector, rendered largest first. A different
// layout means a different image, which is why it is part of hw_hash.
function PartitionTable({ partitions }) {
    if (!partitions || typeof partitions !== 'object') {
        return <p className="text-sm text-gray-500">Partition table not reported.</p>;
    }
    const rows = Object.entries(partitions)
        .map(([name, mb]) => [name, Number(mb) || 0])
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return (
        <div className="card-inset overflow-hidden">
            <table className="w-full text-sm">
                <tbody>
                    {rows.map(([name, mb]) => (
                        <tr key={name} className="border-b border-white/[0.04] last:border-0">
                            <td className="py-1 px-3 font-mono text-[13px] text-gray-200">{name}</td>
                            <td className="py-1 px-3 text-right text-gray-400 tabular-nums">{formatMb(mb)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------

function yesNo(v) {
    if (v === undefined || v === null || v === '') return null;
    return v === true || v === 'true' ? 'yes' : 'no';
}

function formatMb(mb) {
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    if (mb === 0) return '< 1 MB';
    return `${mb} MB`;
}

// JEDEC manufacturer ids seen on TV boxes. Purely a hint; unknown ids show nothing.
function emmcVendor(manfid) {
    if (!manfid) return null;
    const id = String(manfid).toLowerCase().replace(/^0x0*/, '');
    const known = {
        '2': 'SanDisk', '3': 'SanDisk', '11': 'Toshiba', '13': 'Micron', '15': 'Samsung',
        '45': 'SanDisk', '70': 'Kingston', '90': 'SK Hynix', 'fe': 'Micron', 'd6': 'Foresee',
        '88': 'Foresee', '9b': 'YMTC', 'f4': 'Biwin', '6b': 'Phison',
    };
    return known[id] || null;
}

// SDIO vendor ids for the WiFi modules this fleet ships with. Hint only.
function wifiVendor(sdioId) {
    if (!sdioId) return null;
    const vendor = String(sdioId).split(',')[0].split(':')[0].toLowerCase();
    const known = {
        '0020': 'XRadio (Allwinner XR8xx)', '024c': 'Realtek', '02d0': 'Broadcom/Cypress',
        '0271': 'Qualcomm Atheros', '037a': 'MediaTek', '0097': 'TI', '0089': 'Intel',
    };
    return known[vendor] || null;
}
